import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, Mic } from 'lucide-react';

const ChatInput = ({ 
  onSendMessage, 
  darkMode, 
  disabled: externalDisabled,
  voiceEnabled, 
  isListening, 
  startListening, 
  stopListening 
}) => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inputText.trim() && !isLoading && !externalDisabled) {
      setIsLoading(true);
      try {
        await onSendMessage(inputText);
        setInputText('');
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (inputText.trim() && !isLoading && !externalDisabled) {
      setIsLoading(true);
      try {
        await onSendMessage(inputText);
        setInputText('');
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isInputDisabled = isLoading || externalDisabled;
  const isSendDisabled = isLoading || externalDisabled || !inputText.trim();

  return (
    <div className={`p-3 sm:p-4 border-t ${
      darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white/50'
    }`}>
      <div className="flex items-end space-x-2 sm:space-x-3">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.placeholder')}
            disabled={isInputDisabled}
            rows={1}
            className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
              darkMode 
                ? 'bg-gray-700/70 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white/70 border-gray-300 text-gray-900 placeholder-gray-500'
            } ${isInputDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              minHeight: '40px',
              maxHeight: '120px'
            }}
          />
        </div>
        
        <motion.button
          onClick={handleSend}
          disabled={isSendDisabled}
          className={`p-2 sm:p-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center text-sm sm:text-base ${
            isSendDisabled
              ? 'text-gray-400 cursor-not-allowed opacity-50'
              : darkMode
              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-400/10'
              : 'text-blue-600 hover:text-blue-700 hover:bg-blue-600/10'
          }`}
          whileHover={!isSendDisabled ? { scale: 1.02 } : {}}
          whileTap={!isSendDisabled ? { scale: 0.98 } : {}}
          title={t('chat.send')}
        >
          <Send className="w-4 h-4 sm:w-5 sm:h-5" />
        </motion.button>
      </div>
    </div>
  );
};

export default ChatInput; 